import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { adminAPI } from '../../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Skeleton } from '../../components/ui/skeleton';
import { toast } from 'sonner';
import { formatDateTimeIST } from '../../utils/dateUtils';
import { formatCurrency, formatMilli } from '../../utils/formatUtils';
import { 
  Gift, Clock, CheckCircle, XCircle, User, Calendar, Scale, Loader2
} from 'lucide-react';
import { BackButton, PageSkeleton, StatusBadge } from '../../components/common';

export const AdminVouchersPage = () => {
  const navigate = useNavigate();
  const [vouchers, setVouchers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [updatingId, setUpdatingId] = useState(null);

  const fetchVouchers = useCallback(async () => {
    try {
      const res = await adminAPI.getVouchers();
      setVouchers(res.data.vouchers || []);
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error('Access denied');
        navigate('/admin');
      } else {
        toast.error('Failed to load vouchers');
      }
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    fetchVouchers();
  }, [fetchVouchers]);

  const handleMarkCollected = async (voucherId) => {
    setUpdatingId(voucherId);
    try {
      await adminAPI.updateVoucherStatus(voucherId, 'collected');
      toast.success('Voucher marked as collected');
      fetchVouchers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update voucher');
    } finally {
      setUpdatingId(null);
    }
  };

  const handleMarkExpired = async (voucherId) => {
    setUpdatingId(voucherId);
    try {
      await adminAPI.updateVoucherStatus(voucherId, 'expired');
      toast.success('Voucher marked as expired');
      fetchVouchers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update voucher');
    } finally {
      setUpdatingId(null);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'active':
        return <Badge className="bg-green-100 text-green-700">Active</Badge>;
      case 'collected':
        return <Badge className="bg-blue-100 text-blue-700">Collected</Badge>;
      case 'expired':
        return <Badge className="bg-red-100 text-red-700">Expired</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-cream pb-8">
        <div className="bg-white border-b border-border px-4 py-4">
          <div className="max-w-4xl mx-auto flex items-center gap-4">
            <Skeleton className="w-10 h-10 rounded-lg" />
            <Skeleton className="w-48 h-6" />
          </div>
        </div>
        <div className="max-w-4xl mx-auto px-4 py-6 space-y-4">
          {[1, 2, 3].map(i => (
            <Skeleton key={i} className="h-32 w-full" />
          ))}
        </div>
      </div>
    );
  }

  const activeVouchers = vouchers.filter(v => v.status === 'active');
  const collectedVouchers = vouchers.filter(v => v.status === 'collected');
  const expiredVouchers = vouchers.filter(v => v.status === 'expired');

  return (
    <div className="min-h-screen bg-cream pb-8" data-testid="admin-vouchers-page">
      {/* Header */}
      <div className="bg-white border-b border-border px-4 py-4 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <BackButton to="/admin" />
            <h1 className="font-serif text-xl text-navy font-semibold">Gold Redemption Vouchers</h1>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-6">
        {/* Summary Cards */}
        <div className="grid grid-cols-3 gap-4 mb-6">
          <Card className="border-green-200 bg-green-50">
            <CardContent className="p-4 text-center">
              <Gift className="w-6 h-6 text-green-600 mx-auto mb-1" />
              <p className="text-2xl font-bold text-green-700">{activeVouchers.length}</p>
              <p className="text-xs text-green-600">Active (Pending Pickup)</p>
            </CardContent>
          </Card>
          <Card className="border-blue-200 bg-blue-50">
            <CardContent className="p-4 text-center">
              <CheckCircle className="w-6 h-6 text-blue-600 mx-auto mb-1" />
              <p className="text-2xl font-bold text-blue-700">{collectedVouchers.length}</p>
              <p className="text-xs text-blue-600">Collected</p>
            </CardContent>
          </Card>
          <Card className="border-red-200 bg-red-50">
            <CardContent className="p-4 text-center">
              <XCircle className="w-6 h-6 text-red-600 mx-auto mb-1" />
              <p className="text-2xl font-bold text-red-700">{expiredVouchers.length}</p>
              <p className="text-xs text-red-600">Expired</p>
            </CardContent>
          </Card>
        </div>

        {/* Info Card */}
        <Card className="mb-6 bg-amber-50 border-amber-200">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <Gift className="w-5 h-5 text-amber-600 mt-0.5" />
              <div>
                <p className="font-medium text-amber-700">What are Gold Redemption Vouchers?</p>
                <p className="text-sm text-amber-600 mt-1">
                  When users redeem their Millis for physical gold, a voucher is created. 
                  Active vouchers are pending pickup. Once the gold is collected from the store, 
                  mark the voucher as "Collected". Vouchers expire after 30 days if not collected.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Active Vouchers */}
        {activeVouchers.length > 0 && (
          <Card className="mb-6 border-green-200">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg text-green-700 flex items-center gap-2">
                <Clock className="w-5 h-5" />
                Active Vouchers (Pending Pickup)
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {activeVouchers.map(voucher => (
                <div key={voucher.voucher_id} className="border rounded-lg p-4 bg-white">
                  <div className="flex items-start justify-between">
                    <div className="space-y-1">
                      <div className="flex items-center gap-2">
                        {getStatusBadge(voucher.status)}
                        <span className="font-mono text-sm text-muted-foreground">{voucher.voucher_id}</span>
                      </div>
                      <div className="flex items-center gap-4 text-sm">
                        <span className="flex items-center gap-1">
                          <User className="w-4 h-4 text-muted-foreground" />
                          {voucher.user_name || voucher.user_id}
                        </span>
                        <span className="flex items-center gap-1">
                          <Calendar className="w-4 h-4 text-muted-foreground" />
                          {formatDateTimeIST(voucher.created_at)}
                        </span>
                      </div>
                      <div className="flex items-center gap-4 mt-2">
                        <span className="text-lg font-bold text-navy">{voucher.milli_amount?.toFixed(2)} Milli's</span>
                        <span className="flex items-center gap-1 text-purple-600">
                          <Scale className="w-4 h-4" />
                          {voucher.gold_grams?.toFixed(3)}g gold
                        </span>
                      </div>
                      <p className="text-xs text-muted-foreground">
                        Expires: {formatDateTimeIST(voucher.expires_at)}
                      </p>
                    </div>
                    <div className="flex flex-col gap-2">
                      <Button
                        size="sm"
                        onClick={() => handleMarkCollected(voucher.voucher_id)}
                        disabled={updatingId === voucher.voucher_id}
                        className="bg-green-600 hover:bg-green-700"
                      >
                        {updatingId === voucher.voucher_id ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <CheckCircle className="w-4 h-4 mr-1" />
                        )}
                        Mark Collected
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleMarkExpired(voucher.voucher_id)}
                        disabled={updatingId === voucher.voucher_id}
                        className="text-red-600 border-red-200 hover:bg-red-50"
                      >
                        {updatingId === voucher.voucher_id ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <XCircle className="w-4 h-4 mr-1" />
                        )}
                        Mark Expired
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        )}

        {/* Collected Vouchers */}
        {collectedVouchers.length > 0 && (
          <Card className="mb-6 border-blue-200">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg text-blue-700 flex items-center gap-2">
                <CheckCircle className="w-5 h-5" />
                Collected Vouchers
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {collectedVouchers.slice(0, 10).map(voucher => (
                <div key={voucher.voucher_id} className="border rounded-lg p-3 bg-white/50">
                  <div className="flex items-center justify-between">
                    <div className="space-y-1">
                      <div className="flex items-center gap-2">
                        {getStatusBadge(voucher.status)}
                        <span className="font-mono text-xs text-muted-foreground">{voucher.voucher_id}</span>
                      </div>
                      <p className="text-sm">{voucher.user_name || voucher.user_id}</p>
                    </div>
                    <div className="text-right">
                      <p className="font-bold text-navy">{voucher.gold_grams?.toFixed(3)}g</p>
                      <p className="text-xs text-muted-foreground">{formatDateTimeIST(voucher.created_at)}</p>
                    </div>
                  </div>
                </div>
              ))}
              {collectedVouchers.length > 10 && (
                <p className="text-sm text-muted-foreground text-center">
                  ... and {collectedVouchers.length - 10} more
                </p>
              )}
            </CardContent>
          </Card>
        )}

        {/* Expired Vouchers */}
        {expiredVouchers.length > 0 && (
          <Card className="border-red-200">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg text-red-700 flex items-center gap-2">
                <XCircle className="w-5 h-5" />
                Expired Vouchers
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {expiredVouchers.slice(0, 5).map(voucher => (
                <div key={voucher.voucher_id} className="border rounded-lg p-3 bg-white/50">
                  <div className="flex items-center justify-between">
                    <div className="space-y-1">
                      <div className="flex items-center gap-2">
                        {getStatusBadge(voucher.status)}
                        <span className="font-mono text-xs text-muted-foreground">{voucher.voucher_id}</span>
                      </div>
                      <p className="text-sm">{voucher.user_name || voucher.user_id}</p>
                    </div>
                    <div className="text-right">
                      <p className="font-bold text-red-600">{voucher.gold_grams?.toFixed(3)}g</p>
                      <p className="text-xs text-muted-foreground">Expired: {formatDateTimeIST(voucher.expires_at)}</p>
                    </div>
                  </div>
                </div>
              ))}
              {expiredVouchers.length > 5 && (
                <p className="text-sm text-muted-foreground text-center">
                  ... and {expiredVouchers.length - 5} more
                </p>
              )}
            </CardContent>
          </Card>
        )}

        {vouchers.length === 0 && (
          <Card className="border-dashed">
            <CardContent className="p-8 text-center">
              <Gift className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
              <p className="text-muted-foreground">No redemption vouchers yet</p>
              <p className="text-sm text-muted-foreground mt-1">
                Vouchers will appear here when users redeem their Millis for gold
              </p>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default AdminVouchersPage;
